import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Users, DollarSign, CheckCircle, ListChecks, Expand, X } from "lucide-react";

const DashboardPreview: React.FC = () => {
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#4ba3c7] blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 rounded-full bg-[#FA8C00] blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-0">
          {/* Left column - Content */}
          <div className="lg:w-[38%] xl:w-[35%] lg:pr-8 mb-8 lg:mb-0">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-4xl xl:text-5xl font-bold leading-tight mb-4 text-gray-800 text-left">
                Your Command Center
              </h2>
              <p className="text-xl text-[#FA8C00] font-semibold mb-4 text-left">
                See everything that matters at a glance
              </p>
              <p className="text-lg text-gray-600 mb-8 text-left">
                No more hunting through menus or juggling spreadsheets. Your custom dashboard brings together real-time data exactly how you need it.
              </p>
              
              <div className="space-y-4 flex flex-col items-start">
                {[
                  { 
                    icon: <BarChart className="h-5 w-5 text-[#FA8C00]" />, 
                    title: "Real-time Insights", 
                    desc: "Student enrollments, attendance, and program metrics updated live" 
                  },
                  { 
                    icon: <DollarSign className="h-5 w-5 text-[#4ba3c7]" />, 
                    title: "Financial Overview", 
                    desc: "Revenue tracking, payment status, and billing at your fingertips" 
                  },
                  { 
                    icon: <Users className="h-5 w-5 text-[#7ad4a0]" />, 
                    title: "Quick Actions", 
                    desc: "Jump to any student, class, or task without searching" 
                  },
                  { 
                    icon: <ListChecks className="h-5 w-5 text-[#F75C57]" />, 
                    title: "HivePad Tasks", 
                    desc: "Quick task management connected to dates and families" 
                  }
                ].map((feature, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-3 group w-full justify-start"
                  >
                    <div className="mt-1 p-2 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-110">
                      {feature.icon}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-800 mb-1 text-left">{feature.title}</h3>
                      <p className="text-gray-600 text-sm text-left">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          
          {/* Right column - Dashboard Image */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:w-[62%] xl:w-[65%] relative flex items-start justify-end lg:-mr-20 xl:-mr-28 2xl:-mr-36 lg:-mt-8 lg:-mb-12 xl:-mb-16 2xl:-mb-20"
          >
            <div 
              className="relative group cursor-pointer w-full h-full flex items-start justify-end overflow-hidden"
              onClick={() => setIsImageExpanded(true)}
            >
              <img 
                src="https://media.hivetivity.com/hero%20dashboard2.jpg"
                alt="Hivetivity Dashboard"
                className="w-[140%] h-auto lg:translate-x-[5%] xl:translate-x-[8%] 2xl:translate-x-[10%] object-cover object-left transition-transform duration-300 group-hover:scale-[1.05] origin-top-left"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 rounded-lg flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                  <Expand className="h-6 w-6 text-gray-700" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Image Lightbox Modal */}
      <AnimatePresence>
        {isImageExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
            onClick={() => setIsImageExpanded(false)}
          >
            <div className="min-h-screen flex items-center justify-center p-4 py-20">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="relative max-w-[90vw]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src="https://media.hivetivity.com/hero%20dashboard2.jpg"
                  alt="Hivetivity Dashboard - Expanded View"
                  className="w-full h-auto object-contain rounded-lg shadow-2xl"
                />
              </motion.div>
            </div>
            <button
              onClick={() => setIsImageExpanded(false)}
              className="fixed top-24 right-4 p-3 bg-[#FA8C00] hover:bg-[#FA8C00]/90 rounded-full shadow-lg transition-all duration-200 transform hover:scale-110 z-[100]"
            >
              <X className="h-7 w-7 text-white" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default DashboardPreview;